import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { 
  ArrowLeft,
  Type,
  Image as ImageIcon,
  Palette,
  Save,
  ShoppingCart,
  QrCode,
  Move,
  Trash2,
  Plus,
  RotateCcw,
  Download,
  Eye,
  Loader2,
  Upload,
  X,
  CreditCard,
  Minus,
  Phone,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to normalize text elements - ensure textAlign defaults to 'center'
const normalizeTextElements = (elements) => {
  if (!elements || !Array.isArray(elements)) return [];
  return elements.map(el => ({
    ...el,
    textAlign: el.textAlign || 'center' // Default to center alignment
  }));
};

// Helper to resolve image URLs
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('data:')) return url;
  return `${API_URL}${url}`;
};

// Available fonts
const FONTS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Trebuchet MS', label: 'Trebuchet MS' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
];

// Preset colors
const PRESET_COLORS = [
  '#000000', '#FFFFFF', '#1F2937', '#374151', '#6B7280',
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16',
  '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9',
  '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
  '#EC4899', '#F43F5E'
];

const CardDesigner = () => {
  const { templateId: paramTemplateId } = useParams();
  const [searchParams] = useSearchParams();
  const queryTemplateId = searchParams.get('template');
  const templateId = paramTemplateId || queryTemplateId;
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const frontCanvasRef = useRef(null);
  const backCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [discountTiers, setDiscountTiers] = useState([]);
  
  // Design state
  const [activeSide, setActiveSide] = useState('front');
  const [frontCustomImage, setFrontCustomImage] = useState(null);
  const [backCustomImage, setBackCustomImage] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  const [logoPosition, setLogoPosition] = useState({ x: 85, y: 15, size: 15 }); // Top-right by default
  const [textElements, setTextElements] = useState([
    { id: 'name', text: 'Your Name', x: 50, y: 40, fontSize: 24, fontFamily: 'Inter', color: '#FFFFFF', fontWeight: 'bold', textAlign: 'center' },
    { id: 'title', text: 'Job Title', x: 50, y: 55, fontSize: 14, fontFamily: 'Inter', color: '#FFFFFF', fontWeight: 'normal', textAlign: 'center' },
  ]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [qrPosition, setQrPosition] = useState({ x: 50, y: 50, size: 30 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState(null); // 'text' or 'qr'
  const [designId, setDesignId] = useState(null);

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'saved' | 'error'
  const autoSaveTimerRef = useRef(null);
  const designIdRef = useRef(null); // keep a ref so the debounced fn always sees current value
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (!user) {
      navigate('/shop');
      return;
    }
    loadTemplate();
    loadDiscountTiers();
  }, [templateId, user]);

  const loadDiscountTiers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/quantity-discounts`);
      if (res.ok) setDiscountTiers(await res.json());
    } catch (e) { console.error('Failed to load discounts:', e); }
  };

  const loadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/card-designer/templates/${templateId}`);
      if (response.ok) {
        const data = await response.json();
        setTemplate(data);
        
        // Load template's text elements (with center alignment default)
        if (data.front_text_elements && data.front_text_elements.length > 0) {
          setTextElements(normalizeTextElements(data.front_text_elements));
        }
        
        // Load template's logo if present
        if (data.front_logo_position?.image) {
          setLogoImage(data.front_logo_position.image);
          setLogoPosition({
            x: data.front_logo_position.x || 85,
            y: data.front_logo_position.y || 15,
            size: data.front_logo_position.size || 15
          });
        }
        
        // Load QR position if present
        if (data.back_qr_position) {
          setQrPosition({
            x: data.back_qr_position.x || 50,
            y: data.back_qr_position.y || 50,
            size: data.back_qr_position.size || 30
          });
        }
      } else {
        toast({
          title: "Template not found",
          description: "The selected template doesn't exist",
          variant: "destructive"
        });
        navigate('/shop');
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast({
        title: "Error",
        description: "Failed to load template",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file",
        description: "Please upload an image file",
        variant: "destructive"
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (side === 'front') {
        setFrontCustomImage(event.target.result);
      } else {
        setBackCustomImage(event.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const addTextElement = () => {
    const newElement = {
      id: `text_${Date.now()}`,
      text: 'New Text',
      x: 50,
      y: 70,
      fontSize: 16,
      fontFamily: 'Inter',
      color: '#FFFFFF',
      fontWeight: 'normal',
      textAlign: 'center'
    };
    setTextElements([...textElements, newElement]);
    setSelectedElement(newElement.id);
  };

  const updateTextElement = (id, updates) => {
    setTextElements(textElements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    ));
  };

  const deleteTextElement = (id) => {
    setTextElements(textElements.filter(el => el.id !== id));
    if (selectedElement === id) {
      setSelectedElement(null);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive"
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoImage(event.target.result);
      toast({
        title: "Logo uploaded",
        description: "Drag the logo to reposition it on the card"
      });
    };
    reader.readAsDataURL(file);
  };

  const handleCanvasMouseDown = (e, type, elementId = null) => {
    e.preventDefault();
    setIsDragging(true);
    setDragType(type);
    if (type === 'text' && elementId) {
      setSelectedElement(elementId);
    }
  };

  const handleCanvasMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const canvas = activeSide === 'front' ? frontCanvasRef.current : backCanvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    // Support both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    
    // Clamp values between 5 and 95
    const clampedX = Math.max(5, Math.min(95, x));
    const clampedY = Math.max(5, Math.min(95, y));
    
    if (dragType === 'text' && selectedElement) {
      updateTextElement(selectedElement, { x: clampedX, y: clampedY });
    } else if (dragType === 'logo') {
      setLogoPosition(prev => ({ ...prev, x: clampedX, y: clampedY }));
    } else if (dragType === 'qr') {
      setQrPosition(prev => ({ ...prev, x: clampedX, y: clampedY }));
    }
  }, [isDragging, dragType, selectedElement, activeSide]);

  const handleCanvasMouseUp = () => {
    setIsDragging(false);
    setDragType(null);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleCanvasMouseMove);
      window.addEventListener('mouseup', handleCanvasMouseUp);
      window.addEventListener('touchmove', handleCanvasMouseMove, { passive: false });
      window.addEventListener('touchend', handleCanvasMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleCanvasMouseMove);
        window.removeEventListener('mouseup', handleCanvasMouseUp);
        window.removeEventListener('touchmove', handleCanvasMouseMove);
        window.removeEventListener('touchend', handleCanvasMouseUp);
      };
    }
  }, [isDragging, handleCanvasMouseMove]);

  const saveDesign = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      const token = await user.getIdToken();
      const designData = {
        template_id: templateId,
        front_custom_image: frontCustomImage,
        back_custom_image: backCustomImage,
        text_elements: textElements,
        qr_position: qrPosition
      };

      const method = designId ? 'PUT' : 'POST';
      const url = designId 
        ? `${API_URL}/api/card-designer/my-designs/${designId}`
        : `${API_URL}/api/card-designer/my-designs`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(designData)
      });

      if (response.ok) {
        const data = await response.json();
        if (!designId) {
          setDesignId(data.id);
          designIdRef.current = data.id;
        }
        setAutoSaveStatus('saved');
        toast({
          title: "Design saved!",
          description: "Your card design has been saved"
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save design",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  /* ─── Auto-save (silent, debounced 3 s) ─────────────────── */
  // Use a ref to always hold the LATEST design state — avoids stale-closure bugs
  const latestDesignRef = useRef({});
  useEffect(() => {
    latestDesignRef.current = { 
      templateId, 
      frontCustomImage, 
      backCustomImage, 
      textElements, 
      qrPosition,
      logoImage,
      logoPosition 
    };
  }); // no dep array → runs after every render, always fresh

  const autoSaveDesign = useCallback(async () => {
    if (!user) return;
    setAutoSaveStatus('saving');
    const { 
      templateId: tid, 
      frontCustomImage: fci, 
      backCustomImage: bci,
      textElements: te, 
      qrPosition: qp,
      logoImage: logo,
      logoPosition: logoPos
    } = latestDesignRef.current;
    try {
      const token = await user.getIdToken();
      const currentDesignId = designIdRef.current;
      const designData = {
        template_id: tid,
        front_custom_image: fci,
        back_custom_image: bci,
        text_elements: te,
        qr_position: qp,
        user_logo: logo,
        user_logo_position: logoPos,
        is_draft: true,
        auto_saved: true,
      };

      const method = currentDesignId ? 'PUT' : 'POST';
      const url = currentDesignId
        ? `${API_URL}/api/card-designer/my-designs/${currentDesignId}`
        : `${API_URL}/api/card-designer/my-designs`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(designData),
      });

      if (res.ok) {
        const data = await res.json();
        if (!currentDesignId && data.id) {
          setDesignId(data.id);
          designIdRef.current = data.id;
        }
        setAutoSaveStatus('saved');
        console.log('✅ Auto-saved design, textElements:', te?.length, 'designId:', designIdRef.current);
      } else {
        const errText = await res.text();
        console.error('Auto-save failed:', errText);
        setAutoSaveStatus('error');
      }
    } catch (e) {
      console.error('Auto-save error:', e);
      setAutoSaveStatus('error');
    }
  }, [user]); // only depends on user — latest data always read from ref

  // Trigger debounced auto-save whenever design content changes
  useEffect(() => {
    // Skip the very first render (initial load of template defaults)
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (!user) return;

    setAutoSaveStatus('pending');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveDesign();
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [textElements, frontCustomImage, backCustomImage, qrPosition, logoImage, logoPosition, autoSaveDesign]);

  const addToCart = async () => {
    if (!user) return;
    
    // Save first if not saved
    if (!designId) {
      await saveDesign();
    }
    
    setAddingToCart(true);
    try {
      const token = await user.getIdToken();
      
      // First ensure design is saved
      const saveResponse = await fetch(`${API_URL}/api/card-designer/my-designs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          template_id: templateId,
          front_custom_image: frontCustomImage,
          back_custom_image: backCustomImage,
          text_elements: textElements,
          qr_position: qrPosition
        })
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to save design');
      }

      const savedDesign = await saveResponse.json();
      
      // Add to cart
      const cartResponse = await fetch(`${API_URL}/api/card-designer/add-to-cart?design_id=${savedDesign.id}&quantity=1`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (cartResponse.ok) {
        toast({
          title: "Added to cart!",
          description: "Your custom card design has been added to cart"
        });
        navigate('/cart');
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add to cart",
        variant: "destructive"
      });
    } finally {
      setAddingToCart(false);
    }
  };

  const resetDesign = () => {
    setFrontCustomImage(null);
    setBackCustomImage(null);
    setTextElements([
      { id: 'name', text: 'Your Name', x: 50, y: 40, fontSize: 24, fontFamily: 'Inter', color: '#FFFFFF', fontWeight: 'bold', textAlign: 'center' },
      { id: 'title', text: 'Job Title', x: 50, y: 55, fontSize: 14, fontFamily: 'Inter', color: '#FFFFFF', fontWeight: 'normal', textAlign: 'center' },
    ]);
    setQrPosition({ x: 50, y: 50, size: 30 });
    setSelectedElement(null);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const selectedTextElement = selectedElement 
    ? textElements.find(el => el.id === selectedElement) 
    : null;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/shop')}
                className="text-gray-600"
                data-testid="back-to-shop-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="font-semibold text-gray-900">{template.name}</h1>
                <p className="text-sm text-gray-500">Customize your card</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Auto-save status indicator */}
              {autoSaveStatus !== 'idle' && (
                <span className={`text-[11px] flex items-center gap-1 font-medium px-2 py-1 rounded-full
                  ${autoSaveStatus === 'pending'  ? 'text-gray-400' :
                    autoSaveStatus === 'saving'   ? 'text-amber-500' :
                    autoSaveStatus === 'saved'    ? 'text-emerald-600 bg-emerald-50' :
                    'text-red-500'
                  }`}>
                  {autoSaveStatus === 'pending'  && <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse" />}
                  {autoSaveStatus === 'saving'   && <Loader2 className="w-3 h-3 animate-spin" />}
                  {autoSaveStatus === 'saved'    && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                  {autoSaveStatus === 'error'    && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                  {autoSaveStatus === 'pending'  && 'Unsaved changes…'}
                  {autoSaveStatus === 'saving'   && 'Auto-saving…'}
                  {autoSaveStatus === 'saved'    && 'Auto-saved'}
                  {autoSaveStatus === 'error'    && 'Auto-save failed'}
                </span>
              )}
              <span className="text-lg font-bold text-amber-600">
                {formatPrice(template.price)}
              </span>
              <Button
                variant="outline"
                onClick={saveDesign}
                disabled={saving}
                data-testid="save-design-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save
              </Button>
              <Button
                onClick={addToCart}
                disabled={addingToCart}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="add-to-cart-btn"
              >
                {addingToCart ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ShoppingCart className="w-4 h-4 mr-2" />
                )}
                Add to Cart
              </Button>
              <Button
                onClick={() => setShowOrderModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="order-now-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Order Now
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-4 lg:p-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Canvas Area */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-6">
                  {/* Side Toggle */}
                  <div className="flex justify-center gap-4 mb-6">
                    <Button
                      variant={activeSide === 'front' ? 'default' : 'outline'}
                      onClick={() => setActiveSide('front')}
                      data-testid="front-side-btn"
                    >
                      Front Side
                    </Button>
                    <Button
                      variant={activeSide === 'back' ? 'default' : 'outline'}
                      onClick={() => setActiveSide('back')}
                      data-testid="back-side-btn"
                    >
                      Back Side
                    </Button>
                  </div>

                  {/* Canvas Preview - Full edge-to-edge card display */}
                  <div className="flex justify-center">
                    <div 
                      ref={activeSide === 'front' ? frontCanvasRef : backCanvasRef}
                      className="relative w-full max-w-2xl aspect-[1.6/1] overflow-hidden shadow-2xl cursor-crosshair select-none border-2 border-gray-200"
                      style={{
                        backgroundImage: activeSide === 'front' 
                          ? `url(${frontCustomImage || resolveImageUrl(template.front_bg_image) || resolveImageUrl(template.front_image)})` 
                          : `url(${backCustomImage || resolveImageUrl(template.back_bg_image) || resolveImageUrl(template.back_image)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                      data-testid={`${activeSide}-canvas`}
                    >
                      {/* Dark overlay for better text visibility */}
                      <div className="absolute inset-0 bg-black/20 pointer-events-none"></div>
                      
                      {/* Text Elements (Front only) */}
                      {activeSide === 'front' && textElements.map((element) => (
                        <div
                          key={element.id}
                          className={`absolute cursor-move transition-all touch-none ${
                            selectedElement === element.id 
                              ? 'ring-2 ring-amber-500 ring-offset-2 rounded' 
                              : ''
                          }`}
                          style={{
                            left: `${element.x}%`,
                            top: `${element.y}%`,
                            transform: 'translate(-50%, -50%)',
                            fontSize: `${element.fontSize}px`,
                            fontFamily: element.fontFamily,
                            color: element.color,
                            fontWeight: element.fontWeight,
                            textAlign: element.textAlign,
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseDown={(e) => handleCanvasMouseDown(e, 'text', element.id)}
                          onTouchStart={(e) => { e.preventDefault(); handleCanvasMouseDown(e, 'text', element.id); }}
                          onClick={() => setSelectedElement(element.id)}
                          data-testid={`text-element-${element.id}`}
                        >
                          {element.text}
                        </div>
                      ))}
                      
                      {/* Logo Image (Front only) */}
                      {activeSide === 'front' && logoImage && (
                        <div
                          className={`absolute cursor-move rounded-lg overflow-hidden shadow-lg touch-none ${
                            dragType === 'logo' ? 'ring-2 ring-amber-500 ring-offset-2' : ''
                          }`}
                          style={{
                            left: `${logoPosition.x}%`,
                            top: `${logoPosition.y}%`,
                            transform: 'translate(-50%, -50%)',
                            width: `${logoPosition.size}%`,
                            aspectRatio: '1'
                          }}
                          onMouseDown={(e) => handleCanvasMouseDown(e, 'logo')}
                          onTouchStart={(e) => { e.preventDefault(); handleCanvasMouseDown(e, 'logo'); }}
                          data-testid="logo-element"
                        >
                          <img 
                            src={logoImage} 
                            alt="Logo" 
                            className="w-full h-full object-contain bg-white/90 p-1 rounded"
                            draggable={false}
                          />
                        </div>
                      )}
                      
                      {/* QR Code Placeholder (Back only) */}
                      {activeSide === 'back' && (
                        <div
                          className={`absolute cursor-move bg-white p-2 rounded-lg shadow-lg touch-none ${
                            dragType === 'qr' ? 'ring-2 ring-amber-500' : ''
                          }`}
                          style={{
                            left: `${qrPosition.x}%`,
                            top: `${qrPosition.y}%`,
                            transform: 'translate(-50%, -50%)',
                            width: `${qrPosition.size}%`,
                            aspectRatio: '1'
                          }}
                          onMouseDown={(e) => handleCanvasMouseDown(e, 'qr')}
                          onTouchStart={(e) => { e.preventDefault(); handleCanvasMouseDown(e, 'qr'); }}
                          data-testid="qr-placeholder"
                        >
                          <div className="w-full h-full bg-gray-100 rounded flex flex-col items-center justify-center">
                            <QrCode className="w-1/2 h-1/2 text-gray-400" />
                            <span className="text-[8px] text-gray-400 mt-1">QR Code</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Instructions */}
                  <p className="text-center text-sm text-gray-500 mt-4">
                    {activeSide === 'front' 
                      ? 'Click and drag text to reposition. Select text to edit.' 
                      : 'Drag the QR code to reposition it on your card.'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Controls Panel */}
            <div className="space-y-4">
              {/* Upload Custom Image */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ImageIcon className="w-4 h-4 text-gray-600" />
                    <h3 className="font-medium text-gray-900">Background Image</h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-3">
                    Upload a custom image for the {activeSide} side
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, activeSide)}
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-image-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Image
                  </Button>
                  {((activeSide === 'front' && frontCustomImage) || 
                    (activeSide === 'back' && backCustomImage)) && (
                    <Button
                      variant="ghost"
                      className="w-full mt-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => activeSide === 'front' 
                        ? setFrontCustomImage(null) 
                        : setBackCustomImage(null)}
                      data-testid="remove-image-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Remove Custom Image
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Logo Upload (Front Side Only) */}
              {activeSide === 'front' && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <ImageIcon className="w-4 h-4 text-gray-600" />
                      <h3 className="font-medium text-gray-900">Company Logo</h3>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      Upload your company logo (PNG with transparent background recommended)
                    </p>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => logoInputRef.current?.click()}
                      data-testid="upload-logo-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo
                    </Button>
                    {logoImage && (
                      <>
                        <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <img 
                              src={logoImage} 
                              alt="Logo preview" 
                              className="w-12 h-12 object-contain bg-white rounded border"
                            />
                            <div className="flex-1">
                              <p className="text-sm text-gray-600">Logo Size</p>
                              <input
                                type="range"
                                min="5"
                                max="30"
                                value={logoPosition.size}
                                onChange={(e) => setLogoPosition(prev => ({ 
                                  ...prev, 
                                  size: parseInt(e.target.value) 
                                }))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          className="w-full mt-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setLogoImage(null)}
                          data-testid="remove-logo-btn"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Remove Logo
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Text Controls (Front Side Only) */}
              {activeSide === 'front' && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Type className="w-4 h-4 text-gray-600" />
                        <h3 className="font-medium text-gray-900">Text Elements</h3>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={addTextElement}
                        data-testid="add-text-btn"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    </div>

                    {/* Text Element List */}
                    <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                      {textElements.map((el) => (
                        <div 
                          key={el.id}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                            selectedElement === el.id 
                              ? 'bg-amber-50 border border-amber-200' 
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                          onClick={() => setSelectedElement(el.id)}
                          data-testid={`text-item-${el.id}`}
                        >
                          <span className="text-sm truncate flex-1">{el.text}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteTextElement(el.id);
                            }}
                            data-testid={`delete-text-${el.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Selected Text Editor */}
                    {selectedTextElement && (
                      <div className="space-y-4 pt-4 border-t">
                        <div>
                          <Label className="text-xs">Text Content</Label>
                          <Input
                            value={selectedTextElement.text}
                            onChange={(e) => updateTextElement(selectedElement, { text: e.target.value })}
                            className="mt-1"
                            data-testid="text-content-input"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Font</Label>
                            <Select
                              value={selectedTextElement.fontFamily}
                              onValueChange={(value) => updateTextElement(selectedElement, { fontFamily: value })}
                            >
                              <SelectTrigger className="mt-1" data-testid="font-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FONTS.map((font) => (
                                  <SelectItem key={font.value} value={font.value}>
                                    {font.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Size ({selectedTextElement.fontSize}px)</Label>
                            <Slider
                              value={[selectedTextElement.fontSize]}
                              onValueChange={([value]) => updateTextElement(selectedElement, { fontSize: value })}
                              min={10}
                              max={48}
                              step={1}
                              className="mt-3"
                              data-testid="font-size-slider"
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Weight</Label>
                          <div className="flex gap-2 mt-1">
                            <Button
                              size="sm"
                              variant={selectedTextElement.fontWeight === 'normal' ? 'default' : 'outline'}
                              onClick={() => updateTextElement(selectedElement, { fontWeight: 'normal' })}
                              className="flex-1"
                            >
                              Normal
                            </Button>
                            <Button
                              size="sm"
                              variant={selectedTextElement.fontWeight === 'bold' ? 'default' : 'outline'}
                              onClick={() => updateTextElement(selectedElement, { fontWeight: 'bold' })}
                              className="flex-1"
                            >
                              Bold
                            </Button>
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Color</Label>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {PRESET_COLORS.map((color) => (
                              <button
                                key={color}
                                className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                                  selectedTextElement.color === color 
                                    ? 'border-amber-500 scale-110' 
                                    : 'border-transparent'
                                }`}
                                style={{ backgroundColor: color }}
                                onClick={() => updateTextElement(selectedElement, { color })}
                                data-testid={`color-${color}`}
                              />
                            ))}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Label className="text-xs">Custom:</Label>
                            <Input
                              type="color"
                              value={selectedTextElement.color}
                              onChange={(e) => updateTextElement(selectedElement, { color: e.target.value })}
                              className="w-10 h-8 p-0 border-0"
                              data-testid="custom-color-input"
                            />
                            <Input
                              value={selectedTextElement.color}
                              onChange={(e) => updateTextElement(selectedElement, { color: e.target.value })}
                              className="flex-1 font-mono text-xs"
                              placeholder="#000000"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* QR Controls (Back Side Only) */}
              {activeSide === 'back' && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <QrCode className="w-4 h-4 text-gray-600" />
                      <h3 className="font-medium text-gray-900">QR Code Settings</h3>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">
                      The QR code will link to your bio page when the card is ordered.
                    </p>
                    
                    <div>
                      <Label className="text-xs">QR Size ({qrPosition.size}%)</Label>
                      <Slider
                        value={[qrPosition.size]}
                        onValueChange={([value]) => setQrPosition(prev => ({ ...prev, size: value }))}
                        min={15}
                        max={50}
                        step={1}
                        className="mt-3"
                        data-testid="qr-size-slider"
                      />
                    </div>

                    <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                      <p className="text-xs text-amber-800">
                        <strong>Note:</strong> The dummy QR code shown here will be replaced with your actual bio page QR code after ordering.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <Card>
                <CardContent className="p-4">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={resetDesign}
                    data-testid="reset-design-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset Design
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Order Modal */}
      {showOrderModal && (
        <OrderModal
          template={template}
          designId={designId}
          user={user}
          discountTiers={discountTiers}
          onClose={() => setShowOrderModal(false)}
        />
      )}
    </Layout>
  );
};


/* ══════════════════════════════════════════════════════════════
   ORDER MODAL for Card Designer
══════════════════════════════════════════════════════════════ */
const OrderModal = ({ template, designId, user, discountTiers, onClose }) => {
  const { toast } = useToast();
  const [csvFile, setCsvFile] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [paymentStep, setPaymentStep] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [success, setSuccess] = useState(null);
  const csvInputRef = useRef(null);

  const basePrice = template?.price || 999;
  const finalPrice = template?.final_price || template?.price || 999;
  const csvRequired = quantity > 1;

  const calculatePricing = () => {
    let quantityDiscountPercent = 0;
    let tierName = '1 card';
    for (const tier of discountTiers) {
      const minQ = tier.min_quantity;
      const maxQ = tier.max_quantity;
      if (maxQ === null) {
        if (quantity >= minQ) { quantityDiscountPercent = tier.discount_percent; tierName = `${minQ}+`; }
      } else if (quantity >= minQ && quantity <= maxQ) { quantityDiscountPercent = tier.discount_percent; tierName = `${minQ}-${maxQ}`; break; }
    }

    const templateDiscount = basePrice > finalPrice ? Math.round(((basePrice - finalPrice) / basePrice) * 100) : 0;
    const subtotal = finalPrice * quantity;
    const discountAmount = Math.round(subtotal * (quantityDiscountPercent / 100));
    const total = subtotal - discountAmount;

    return { basePrice, finalPrice, templateDiscount, quantity, subtotal, quantityDiscountPercent, discountAmount, total, tierName };
  };

  const pricing = calculatePricing();

  const handleCsvFile = (files) => {
    const file = files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { setError('Please upload a CSV file.'); return; }
    setError('');
    setCsvFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      const lines = e.target.result.split('\n').filter(l => l.trim());
      const rowCount = lines.length - 1;
      if (rowCount >= 1) setQuantity(rowCount);
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    if (!phone.trim() || !/^[6-9]\d{9}$/.test(phone.trim())) { setError('Enter valid 10-digit mobile number.'); return; }
    if (csvRequired && !csvFile) { setError('CSV required for bulk orders (qty > 1).'); return; }

    setSubmitting(true);
    setError('');

    try {
      const token = await user.getIdToken();
      const formData = new FormData();
      formData.append('design_type', 'custom');
      formData.append('template_id', template?.id || '');
      formData.append('quantity', quantity);
      formData.append('phone', phone.trim());
      formData.append('notes', notes.trim());
      formData.append('base_price', basePrice);
      formData.append('final_price', finalPrice);
      if (csvFile) formData.append('csv_file', csvFile);

      const res = await fetch(`${API_URL}/api/shop/order/create`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Order failed');

      setOrderData(data);
      setPaymentStep(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async () => {
    if (!orderData?.order_id) return;
    setProcessingPayment(true);
    setError('');

    try {
      // Load Razorpay script first
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Failed to load payment gateway');
      
      const token = await user.getIdToken();
      const payRes = await fetch(`${API_URL}/api/shop/order/${orderData.order_id}/create-payment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const payData = await payRes.json();
      if (!payRes.ok) throw new Error(payData.detail);

      const options = {
        key: payData.razorpay_key_id,
        amount: payData.amount,
        currency: payData.currency,
        name: 'Piper Card',
        description: `Order: ${orderData.quantity} Cards`,
        order_id: payData.razorpay_order_id,
        handler: async (response) => {
          try {
            const verifyRes = await fetch(
              `${API_URL}/api/shop/order/${orderData.order_id}/verify-payment?razorpay_order_id=${response.razorpay_order_id}&razorpay_payment_id=${response.razorpay_payment_id}&razorpay_signature=${response.razorpay_signature}`,
              { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
            );
            const verifyData = await verifyRes.json();
            if (!verifyRes.ok) throw new Error(verifyData.detail);
            setSuccess({ order_id: orderData.order_id, quantity: orderData.quantity, total: payData.total_price, message: verifyData.message });
            setPaymentStep(false);
          } catch (err) { setError(err.message); }
        },
        prefill: { email: user.email, contact: phone },
        theme: { color: '#7C3AED' },
        modal: { ondismiss: () => setProcessingPayment(false) }
      };
      new window.Razorpay(options).open();
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Load Razorpay script dynamically
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="font-bold text-gray-900">Order Your Cards</h2>
            <p className="text-xs text-gray-500">{template?.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-5">
          {success ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-9 h-9 text-emerald-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Payment Successful!</h3>
              <p className="text-sm text-gray-500 mb-4">{success.message}</p>
              <Button onClick={onClose} className="w-full bg-gray-900 hover:bg-gray-800">Done</Button>
            </div>
          ) : paymentStep ? (
            <div>
              <div className="bg-violet-50 rounded-xl p-4 mb-4">
                <h3 className="font-bold text-gray-900 mb-3">Order Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Quantity</span><span>{orderData.quantity} cards</span></div>
                  {orderData.pricing?.quantity_discount_percent > 0 && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Bulk Discount</span>
                      <span>-{orderData.pricing.quantity_discount_percent}%</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t text-lg">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-violet-600">₹{orderData.pricing?.total_price?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              {error && <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3"><AlertCircle className="w-4 h-4" />{error}</div>}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setPaymentStep(false)} className="flex-1">Back</Button>
                <Button onClick={handlePayment} disabled={processingPayment} className="flex-1 bg-violet-600 hover:bg-violet-700">
                  {processingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay ₹{orderData.pricing?.total_price?.toLocaleString()}</>}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Quantity */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity</label>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}><Minus className="w-4 h-4" /></Button>
                  <Input type="number" min={1} value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="w-20 text-center text-lg font-bold" />
                  <Button variant="outline" size="icon" onClick={() => setQuantity(quantity + 1)}><Plus className="w-4 h-4" /></Button>
                </div>
              </div>

              {/* Discount Tiers */}
              <div className="bg-violet-50 rounded-xl p-3 mb-4">
                <p className="text-xs font-bold text-gray-700 mb-2">Bulk Discounts</p>
                <div className="flex flex-wrap gap-2">
                  {discountTiers.map((tier, i) => (
                    <span key={i} className={`px-2 py-1 rounded-full text-xs font-medium ${pricing.tierName === (tier.max_quantity ? `${tier.min_quantity}-${tier.max_quantity}` : `${tier.min_quantity}+`) ? 'bg-violet-600 text-white' : 'bg-white text-gray-600'}`}>
                      {tier.min_quantity}{tier.max_quantity ? `-${tier.max_quantity}` : '+'}: {tier.discount_percent}% off
                    </span>
                  ))}
                </div>
              </div>

              {/* CSV (if bulk) */}
              {csvRequired && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2"><FileSpreadsheet className="w-4 h-4 inline mr-1" />Upload CSV *</label>
                  <p className="text-xs text-gray-500 mb-2">Each row = 1 personalized card</p>
                  <div
                    onClick={() => csvInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer ${csvFile ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 hover:border-violet-300'}`}
                  >
                    <input ref={csvInputRef} type="file" accept=".csv" className="hidden" onChange={e => handleCsvFile(e.target.files)} />
                    {csvFile ? (
                      <div className="flex items-center justify-center gap-2 text-sm text-emerald-700">
                        <FileSpreadsheet className="w-5 h-5" /><span>{csvFile.name}</span>
                        <button onClick={e => { e.stopPropagation(); setCsvFile(null); }}><X className="w-4 h-4 text-red-500" /></button>
                      </div>
                    ) : (
                      <><FileSpreadsheet className="w-6 h-6 text-gray-300 mx-auto mb-1" /><p className="text-sm text-gray-600">Upload CSV</p></>
                    )}
                  </div>
                </div>
              )}

              {/* Phone */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2"><Phone className="w-4 h-4 inline mr-1" />Mobile Number *</label>
                <Input value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit number" type="tel" />
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Notes (optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Special instructions..." rows={2} className="w-full text-sm border rounded-lg px-3 py-2 resize-none" />
              </div>

              {/* Price Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Price per card</span>
                    <span>
                      {pricing.templateDiscount > 0 && <span className="text-gray-400 line-through mr-2">₹{pricing.basePrice}</span>}
                      ₹{pricing.finalPrice}
                    </span>
                  </div>
                  <div className="flex justify-between"><span className="text-gray-600">{quantity} × ₹{pricing.finalPrice}</span><span>₹{pricing.subtotal.toLocaleString()}</span></div>
                  {pricing.quantityDiscountPercent > 0 && (
                    <div className="flex justify-between text-emerald-600"><span>Bulk Discount ({pricing.quantityDiscountPercent}%)</span><span>-₹{pricing.discountAmount.toLocaleString()}</span></div>
                  )}
                  <div className="flex justify-between pt-2 border-t text-lg"><span className="font-bold">Total</span><span className="font-bold text-violet-600">₹{pricing.total.toLocaleString()}</span></div>
                </div>
              </div>

              {error && <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3"><AlertCircle className="w-4 h-4" />{error}</div>}

              <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-violet-600 hover:bg-violet-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Proceed to Payment</>}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardDesigner;
